import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A minimal native checkbox styled to match the rest of the primitives.
 *
 * Not one of the primitives named in the original brief, but ConfigForm's
 * `boolean` field type needs a toggle control and none of the listed
 * primitives provide one — this fills that gap rather than reaching for a
 * text input to represent a yes/no value.
 */
export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, onCheckedChange, ...props }, ref) => (
    <span className={cn('relative inline-flex h-5 w-5 shrink-0 items-center justify-center', className)}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="peer absolute inset-0 h-5 w-5 cursor-pointer appearance-none rounded border border-input bg-background checked:border-primary checked:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        {...props}
      />
      <Check className="pointer-events-none h-3.5 w-3.5 text-primary-foreground opacity-0 peer-checked:opacity-100" />
    </span>
  ),
);
Checkbox.displayName = 'Checkbox';
